import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getTenantsApi,
  getTenantApi,
  getPropertyListApi,
  getInvoiceListApi,
  getUtilityListApi,
  getBillingOptionsApi,
  getIncentivePlanOptionsApi,
} from '../../../apis/apis'

import {
  type Tenant,
  type TenantItem,
  type ServiceAccount,
  type Paginated,
  type GetTenantsArgs,
  type GetTenantsData,
  type GetTenantArgs,
  type GetInvoicesListData,
  type CollectionsInvoice,
  type Utility,
  BillingOptionsResult,
  BillingOptionObject,
  IncentivePlanOptionsResult,
  IncentivePlanOptionObject,
} from '../../../apis/types'
import { formatTitleCase } from '../../../util/string'
import { sortByKingAccountNumber } from './tenantListRows'
import type { RootState, AppDispatch } from '../../../shared/redux/store'

const SLICE_NAME = 'TENANTS_SLICE'

export const getTenants = createAsyncThunk<
  GetTenantsData,
  GetTenantsArgs,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getTenants`, async (query, { rejectWithValue }) => {
  try {
    const response = await getTenantsApi(query)
    return response
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export interface StoredTenant extends Tenant {
  serviceAccounts: Array<ServiceAccount & { invoices: GetInvoicesListData; invoicesEnriched?: boolean }>
}

function seedInvoiceSummaryFromAccount(account: ServiceAccount): GetInvoicesListData['summary'] {
  return {
    balanceTotal: String(account.billedBalanceTenantToCorp ?? 0),
    balanceDue: String(account.dueBalanceTenantToCorp ?? 0),
  }
}

const getTenantInflight = new Map<string, Promise<StoredTenant>>()

function invoiceFieldsChanged(
  account: StoredTenant['serviceAccounts'][number],
  previous: StoredTenant['serviceAccounts'][number]
): boolean {
  return (
    account.invoices.summary?.balanceDue !== previous.invoices.summary?.balanceDue ||
    account.invoices.summary?.balanceTotal !== previous.invoices.summary?.balanceTotal ||
    account.currentInvoice?.id !== previous.currentInvoice?.id
  )
}

async function fetchTenant(tenantId: string): Promise<StoredTenant> {
  // Invoice lists load in TabInvoices when that tab is selected for an account.
  const { serviceAccounts, ...rest } = await getTenantApi(tenantId)

  const accounts: StoredTenant['serviceAccounts'] = sortByKingAccountNumber(serviceAccounts).map((account) => {
    const seededSummary = seedInvoiceSummaryFromAccount(account)

    return {
      ...account,
      invoices: {
        results: [],
        page: 0,
        resultsPerPage: 5,
        // Placeholder until TabInvoices enrich; tab badge omitted until invoicesEnriched.
        total: account.currentInvoice != null ? 1 : 0,
        summary: seededSummary,
      },
      invoicesEnriched: false,
    }
  })

  return { ...rest, serviceAccounts: accounts }
}

export const getTenant = createAsyncThunk<
  StoredTenant,
  GetTenantArgs,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getTenant`, async (tenantId, { rejectWithValue }) => {
  let promise = getTenantInflight.get(tenantId)
  if (promise == null) {
    promise = fetchTenant(tenantId).finally(() => {
      if (getTenantInflight.get(tenantId) === promise) {
        getTenantInflight.delete(tenantId)
      }
    })
    getTenantInflight.set(tenantId, promise)
  }

  try {
    return await promise
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getPropertyList = createAsyncThunk<
  Array<{
    id: string
    nameLocation: string
    name: string
    location: string
  }>,
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  void,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getPropertyList`, async (_, { rejectWithValue }) => {
  try {
    const properties = await getPropertyListApi()
    return properties.map((p) => {
      const [name, location] = p.name.split(' - ')
      return {
        id: p.id,
        nameLocation: p.name,
        name,
        location,
      }
    })
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getUtilityList = createAsyncThunk<
  Utility[],
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  void,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getUtilityList`, async (_, { rejectWithValue }) => {
  try {
    const utilities = await getUtilityListApi()
    return utilities
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getInvoicesByTenantId = createAsyncThunk<
  GetInvoicesListData,
  string,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getInvoicesByTenantId`, async (tenantId, { rejectWithValue }) => {
  try {
    const response = await getInvoiceListApi({ tenantId, resultsPerPage: 5 })
    return response
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getBillingOptions = createAsyncThunk<
  BillingOptionsResult,
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  void,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getBillingOptions`, async (_, { rejectWithValue }) => {
  try {
    const res = await getBillingOptionsApi()
    const toObject = (str: string): BillingOptionObject => ({
      [str]: formatTitleCase(str),
    })

    const titleCaseRes: BillingOptionsResult = {
      utilityBillTypes: (res.utilityBillTypes ?? []).map(toObject),
      kingBillTypes: (res.kingBillTypes ?? []).map(toObject),
      enrollmentStatuses: (res.enrollmentStatuses ?? []).map(toObject),
      billingMode: (res.billingMode ?? []).map(toObject),
      billingStatusOptions: (res.billingStatusOptions ?? []).map(toObject),
      billingStatusReasons: (res.billingStatusReasons ?? []).map(toObject),
      accountStatusOptions: (res.accountStatusOptions ?? []).map(toObject),
    }

    return titleCaseRes
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

export const getIncentivePlanOptions = createAsyncThunk<
  IncentivePlanOptionsResult,
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  void,
  {
    state: RootState
    dispatch: AppDispatch
    rejectValue: { err: boolean }
  }
>(`${SLICE_NAME}/getIncentivePlanOptions`, async (_, { rejectWithValue }) => {
  try {
    const res = await getIncentivePlanOptionsApi()
    const toObject = (str: string): IncentivePlanOptionObject => ({
      [str]: formatTitleCase(str),
    })

    const titleCaseRes: IncentivePlanOptionsResult = {
      incentivePlanTypes: res.incentivePlanTypes.map(toObject),
      incentivePlanStatuses: res.incentivePlanStatuses.map(toObject),
      incentivePlanCadences: res.incentivePlanCadences.map(toObject),
      incentivePlanInstallmentStatuses: res.incentivePlanInstallmentStatuses.map(toObject),
    }

    return titleCaseRes
  } catch (err) {
    return rejectWithValue({
      err: err !== undefined,
    })
  }
})

interface state {
  loading: boolean
  error: boolean
  refreshingTenant: boolean
  requestedTenantId?: string
  tenants?: Paginated<TenantItem>
  tenant?: StoredTenant
  properties: Array<{
    id: string
    nameLocation: string
    name: string
    location: string
  }>
  utilities: Utility[]
  invoices: {
    loading: boolean
    error: boolean
    results?: Paginated<CollectionsInvoice>
  }
  billingOptions?: BillingOptionsResult
  incentivePlanOptions?: IncentivePlanOptionsResult
}

const initialState: state = {
  loading: false,
  error: false,
  refreshingTenant: false,
  tenants: undefined,
  tenant: undefined,
  properties: [],
  utilities: [],
  invoices: {
    loading: false,
    error: false,
    results: undefined,
  },
  billingOptions: undefined,
  incentivePlanOptions: undefined,
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    mergeAccountInvoices: (
      state,
      action: {
        payload: {
          accountId: string
          invoices: GetInvoicesListData
        }
      }
    ) => {
      const account = state.tenant?.serviceAccounts.find((sa) => sa.id === action.payload.accountId)
      if (account == null) {
        return
      }
      account.invoices = action.payload.invoices
      account.invoicesEnriched = true
    },
    setRequestedTenantId: (state, action: { payload: string }) => {
      state.requestedTenantId = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTenants.pending, (state) => {
      state.loading = true
      state.error = false
    })
    builder.addCase(getTenants.fulfilled, (state, { payload }) => {
      state.loading = false
      state.error = false
      state.tenants = payload
    })
    builder.addCase(getTenants.rejected, (state, { payload, error }) => {
      state.loading = false

      if (payload != null) {
        state.error = payload.err
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(getTenant.pending, (state: state, { meta }) => {
      if (state.requestedTenantId == null) {
        state.requestedTenantId = meta.arg
      }
      state.loading = state.tenant === undefined
      state.refreshingTenant = state.tenant?.id === meta.arg && state.requestedTenantId === meta.arg
      state.error = false
    })
    builder.addCase(getTenant.fulfilled, (state, { payload, meta }) => {
      if (meta.arg !== state.requestedTenantId || payload.id !== meta.arg) {
        return
      }
      state.loading = false
      state.refreshingTenant = false
      state.error = false
      const previousAccounts = state.tenant?.id === payload.id ? state.tenant?.serviceAccounts : undefined
      const serviceAccounts =
        previousAccounts == null
          ? payload.serviceAccounts
          : payload.serviceAccounts.map((account) => {
              const previous = previousAccounts.find((sa) => sa.id === account.id)
              if (previous == null) {
                return account
              }
              const shouldReenrich = invoiceFieldsChanged(account, previous)
              if (account.invoices.results.length === 0 && previous.invoices.results.length > 0) {
                return {
                  ...account,
                  invoices: {
                    ...previous.invoices,
                    summary: account.invoices.summary ?? previous.invoices.summary,
                  },
                  invoicesEnriched: shouldReenrich ? false : previous.invoicesEnriched === true,
                }
              }
              if (previous.invoicesEnriched === true && account.invoices.results.length === 0) {
                return {
                  ...account,
                  invoices: shouldReenrich
                    ? account.invoices
                    : {
                        ...previous.invoices,
                        summary: account.invoices.summary ?? previous.invoices.summary,
                      },
                  invoicesEnriched: shouldReenrich ? false : true,
                }
              }
              return account
            })
      state.tenant = { ...payload, serviceAccounts }
    })
    builder.addCase(getTenant.rejected, (state, { payload, error, meta }) => {
      if (meta.arg !== state.requestedTenantId) {
        return
      }
      state.loading = false
      state.refreshingTenant = false

      if (payload != null) {
        state.error = payload.err
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(getPropertyList.pending, (state) => {
      state.loading = true
      state.error = false
    })
    builder.addCase(getPropertyList.fulfilled, (state, { payload }) => {
      state.loading = false
      state.error = false
      state.properties = payload
    })
    builder.addCase(getPropertyList.rejected, (state: state, { payload, error }) => {
      state.loading = false

      if (payload != null) {
        state.error = payload.err
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(getUtilityList.pending, (state) => {
      state.loading = true
      state.error = false
    })
    builder.addCase(getUtilityList.fulfilled, (state, { payload }) => {
      state.loading = false
      state.error = false
      state.utilities = payload
    })
    builder.addCase(getUtilityList.rejected, (state: state, { payload, error }) => {
      state.loading = false

      if (payload != null) {
        state.error = payload.err
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(getInvoicesByTenantId.pending, (state) => {
      state.invoices.loading = true
      state.invoices.error = false
    })
    builder.addCase(getInvoicesByTenantId.fulfilled, (state, { payload }) => {
      state.invoices.loading = false
      state.invoices.error = false
      state.invoices.results = payload
    })
    builder.addCase(getInvoicesByTenantId.rejected, (state, { payload, error }) => {
      state.invoices.loading = false

      if (payload != null) {
        state.error = payload.err
      } else {
        state.error = error !== undefined
      }
    })

    builder.addCase(getBillingOptions.pending, () => {
      // Secondary enum fetch — do not flip the global tenant loading/error flags.
    })
    builder.addCase(getBillingOptions.fulfilled, (state, { payload }) => {
      state.billingOptions = payload
    })
    builder.addCase(getBillingOptions.rejected, () => {
      // Leave billingOptions undefined; do not blank the tenant page via state.error.
    })
    builder.addCase(getIncentivePlanOptions.pending, () => {
      // Secondary enum fetch — do not flip the global tenant loading/error flags.
    })
    builder.addCase(getIncentivePlanOptions.fulfilled, (state, { payload }) => {
      state.incentivePlanOptions = payload
    })
    builder.addCase(getIncentivePlanOptions.rejected, () => {
      // Leave incentivePlanOptions undefined; do not blank the tenant page via state.error.
    })
  },
})

export const { mergeAccountInvoices, setRequestedTenantId } = slice.actions
export const tenantsReducer = slice.reducer;
